if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      let added = false;
      let responseReceived = false;
      const controller = new AbortController();
      const requestTimeout = setTimeout(() => controller.abort(), 20000);

      try {
        const formData = new FormData(this.form);
        let sections = 'cart-icon-bubble';
        if (this.cartDrawer) {
          sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
        }

        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sections);

        const fetchRequestOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData,
          signal: controller.signal
        };

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const responseText = await response.text();
        let data;

        try {
          data = JSON.parse(responseText);
        } catch {
          throw new Error(theme.strings.cartError);
        }
        if (!data || typeof data !== 'object') throw new Error(theme.strings.cartError);
        responseReceived = true;
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.values(data.errors).map((item) => (Array.isArray(item) ? item.join(', ') : String(item)));
        }

        if (!response.ok || data.status) {
          this.setErrorMsgState(error || theme.strings.cartError);
          throw new Error(response.status);
        }
        const addedItems = Array.isArray(data.items) ? data.items : [data];
        if (!addedItems.length || !addedItems.every((item) => item?.variant_id && Number.isFinite(item.quantity) && item.quantity > 0)) {
          responseReceived = false;
          throw new Error(theme.strings.cartError);
        }
        added = true;
        clearTimeout(requestTimeout);
        // Shopify returns the new line quantity. No pre-add cart read is needed.
        this.notifyCartChange(data, Number(formData.get('quantity') || 1));

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          if (!ProductForm.updateCartIcon(data)) {
            window.location.href = theme.routes.cart;
            return;
          }

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(async () => {
            try {
              // Update cart drawer contents.
              if (this.cartDrawer) {
                await this.cartDrawer.renderContents(
                  data,
                  !quickAddDrawer && theme.settings.afterAtc === 'drawer'
                );
              } else if (window.location.pathname === theme.routes.cart) {
                const cartItems = document.querySelector('cart-items');
                if (cartItems) {
                  if (cartItems.dataset.empty === 'true') {
                    window.location.reload();
                  } else {
                    await cartItems.refresh();
                  }
                }
              }
            } catch {
              // The product is already added. Recover the UI without adding it again.
              window.location.href = theme.routes.cart;
            }
          }, 700);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        if (added) {
          window.location.href = theme.routes.cart;
          return;
        }
        if (!this.errorMsg || this.errorMsg.hidden) {
          this.setErrorMsgState(responseReceived ? theme.strings.cartError : theme.strings.cartUnconfirmed);
        }
        if (this.errorMsg) {
          const cartLink = document.createElement('a');
          cartLink.href = theme.routes.cart;
          cartLink.className = 'underline';
          cartLink.textContent = theme.strings.viewCart;
          this.errorMsg.append(' ', cartLink);
        }
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent || error.message || theme.strings.cartError
          }
        }));
      } finally {
        clearTimeout(requestTimeout);
        // Re-enable 'Add to Cart' button.
        if (added) {
          this.submitBtn.classList.add('is-success');
          setTimeout(() => {
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.classList.remove('is-success');
            this.submitBtn.removeAttribute('aria-disabled');
          }, 1400);
        } else {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
          this.submitBtn.removeAttribute('aria-disabled');
        }
      }
    }

    /** Optional event bookkeeping must never delay the buying flow. */
    async notifyCartChange(data, requestedQuantity) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(`${theme.routes.cart}.js`, { signal: controller.signal });
        if (!response.ok) return;
        const cart = await response.json();
        if (!Array.isArray(cart.items)) return;
        const items = Array.isArray(data.items) ? data.items : [data];
        items.forEach((item) => {
          // A batch has no single requested quantity; do not invent prior line quantities.
          const oldQuantity = Array.isArray(data.items) ? 0 : Math.max(0, item.quantity - requestedQuantity);
          const detail = { cart, variantId: item.variant_id };
          if (oldQuantity > 0) Object.assign(detail, { oldQuantity, newQuantity: item.quantity });
          this.dispatchEvent(new CustomEvent(oldQuantity > 0 ? 'on:line-item:change' : 'on:cart:add', {
            bubbles: true,
            detail
          }));
        });
      } catch {
        // A failed event read does not undo a confirmed add.
      } finally {
        clearTimeout(timeout);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        if (typeof response.sections?.['cart-icon-bubble'] !== 'string') return false;
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
      return true;
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
